import { ErrorState, LoadingState } from '@/components/common';
import {
	PlatformMetrics,
	QuickActions,
	RecentActivity,
	RecentAiScans,
	SubscriptionOverview,
	SystemHealth,
} from '@/components/dashboard';
import { BarChartOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Col, Row, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDashboard } from '@/hooks/dashboard';

const { Title, Text } = Typography;

const Dashboard = () => {
	const { t } = useTranslation('pages');
	const navigate = useNavigate();
	const {
		stats,
		loading,
		error,
		handleRefresh,
		isRefreshing,
	} = useDashboard();

	if (loading && !stats) {
		return <LoadingState message={t('dashboard.loading')} fullScreen />;
	}

	if (error) {
		return (
			<ErrorState
				title={t('dashboard.errorTitle')}
				message={error}
				onRetry={handleRefresh}
				retryText={t('dashboard.retry')}
			/>
		);
	}

	if (!stats) {
		return (
			<ErrorState
				title={t('dashboard.noDataTitle')}
				message={t('dashboard.noDataMessage')}
				type='warning'
			/>
		);
	}

	return (
		<div className='space-y-4'>
			<div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4'>
				<div>
					<Title level={2} className='!mb-1 !font-bold !text-slate-800'>
						{t('dashboard.title')}
					</Title>
					<Text className='text-slate-500'>
						{dayjs().format('dddd, MMMM D, YYYY')}
					</Text>
				</div>

				<div className='flex items-center gap-3'>
					<Button
						icon={<BarChartOutlined />}
						onClick={() => navigate('/analytics')}
						className='rounded-lg'
					>
						{t('dashboard.viewAnalytics')}
					</Button>

					<Button
						icon={<ReloadOutlined />}
						onClick={handleRefresh}
						loading={loading || isRefreshing}
						type='primary'
						className='rounded-lg bg-indigo-600 hover:bg-indigo-500'
					>
						{t('dashboard.refresh')}
					</Button>
				</div>
			</div>

			<PlatformMetrics
				totalUsers={stats.totalUsers}
				totalPets={stats.totalPets}
				aiScansToday={stats.aiScansToday}
				activeSubscriptions={stats.activeSubscriptions}
				loading={loading}
			/>

			<SubscriptionOverview stats={stats} loading={loading} />

			<Row gutter={[16, 16]}>
				<Col xs={24} lg={16}>
					<RecentAiScans scans={stats.recentScans} loading={loading} />
				</Col>

				<Col xs={24} lg={8}>
					<div className='space-y-4'>
						<QuickActions />
						<SystemHealth
							aiScansToday={stats.aiScansToday}
							activeSubscriptions={stats.activeSubscriptions}
							loading={loading}
						/>
					</div>
				</Col>
			</Row>

			<RecentActivity stats={stats} />
		</div>
	);
};

export { Dashboard };
export default Dashboard;
